import React, { useMemo } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Chip,
  Divider as MuiDivider,
  Link,
  Typography as MuiTypography,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import OpacityIcon from "@material-ui/icons/Opacity";
import BubbleChartIcon from "@material-ui/icons/BubbleChart";
import SnapshotReportButtons from "../../../../components/inspectionSnapshot/SnapshotReportButtons";
import BlankTemplateButtons from "../../../../components/inspectionSnapshot/BlankTemplateButtons";
import { fmtDateString } from "../../../../components/inspectionSnapshot/snapshotFormat";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;

const CountChip = ({ count, Icon, title }) => {
  if (!count) return null;
  return (
    <Chip
      size="small"
      color="primary"
      variant="outlined"
      icon={<Icon />}
      label={count}
      title={title}
    />
  );
};

const useInspectionsList = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["field-services-inspections-list"],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/field-services/inspections`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );
};

const InspectionsManagement = () => {
  const { data, isFetching, error } = useInspectionsList();

  const columns = useMemo(
    () => [
      {
        title: "Insp #",
        field: "ir_ndx",
        type: "numeric",
        width: 80,
        filtering: false,
      },
      {
        title: "Date",
        field: "inspection_date",
        defaultSort: "desc",
        render: (row) => fmtDateString(row.inspection_date),
        // Match what the user sees (MM/DD/YYYY) as well as the raw yyyy-MM-dd
        customFilterAndSearch: (term, row) =>
          fmtDateString(row.inspection_date).includes(term) ||
          String(row.inspection_date || "").includes(term),
      },
      { title: "District Well ID", field: "district_well_id" },
      { title: "Well Name", field: "well_name" },
      { title: "State Well #", field: "state_well_num" },
      { title: "Type", field: "ir_type_desc" },
      { title: "Inspected By", field: "inspected_by" },
      {
        title: "Photos",
        field: "photo_count",
        type: "numeric",
        render: (row) => (
          <CountChip
            count={row.photo_count}
            Icon={PhotoCameraIcon}
            title={`${row.photo_count} photo(s) taken on this date`}
          />
        ),
      },
      {
        title: "WL",
        field: "water_level_count",
        type: "numeric",
        render: (row) => (
          <CountChip
            count={row.water_level_count}
            Icon={OpacityIcon}
            title={`${row.water_level_count} water level measurement(s) on this date`}
          />
        ),
      },
      {
        title: "WQ",
        field: "water_quality_count",
        type: "numeric",
        render: (row) => (
          <CountChip
            count={row.water_quality_count}
            Icon={BubbleChartIcon}
            title={`${row.water_quality_count} water quality sampling event(s) on this date`}
          />
        ),
      },
      {
        title: "Snapshot Report",
        sorting: false,
        filtering: false,
        export: false,
        render: (row) => <SnapshotReportButtons irNdx={row.ir_ndx} />,
      },
    ],
    []
  );

  if (error) return "An error has occurred: " + error.message;

  return (
    <React.Fragment>
      <Helmet title="Well Inspections" />
      <Typography variant="h3" gutterBottom display="inline">
        Well Inspections
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Well Inspections</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Alert severity="info" style={{ marginBottom: 16 }}>
        <AlertTitle>About this page and the Snapshot Report</AlertTitle>
        Every inspection entered through the Field Services app is listed below.
        Use the filter row to narrow by well, date, type, or inspector, and the
        buttons on each row to compile everything recorded during that site
        visit into a Snapshot Report — the PDF button opens your browser's print
        dialog (choose "Save as PDF"), and the Word button downloads an editable
        .docx. How the report is assembled:
        <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
          <li>
            <strong>Inspection details</strong> (date, type, inspectors,
            checklist, field notes) belong to that specific inspection record.
          </li>
          <li>
            <strong>Water levels, water quality, and photos</strong> are not
            tied to an inspection in the database — the report and the count
            columns below roll up everything recorded for that <em>well</em> on
            the <em>same calendar day</em> as the inspection. If a well has two
            inspections on the same day, both reports will show the same day's
            data.
          </li>
          <li>
            <strong>Counts</strong>: Photos = gallery photos taken that day; WL
            = water level measurements; WQ = sampling events (one event may
            include several parameters). The report's water quality table
            automatically includes whichever parameters were recorded, not just
            the standard field set.
          </li>
          <li>
            <strong>Well details</strong> (owner, address, GPS, setbacks,
            casing/slab/sleeve, meter, pump, treatment) come from the well's
            current Field Services record, so they reflect the well as it is{" "}
            <em>today</em>, not necessarily as it was on the inspection date.
          </li>
          <li>
            <strong>Meter readings</strong> are recorded monthly, not per visit,
            so the report shows the readings for the inspection's calendar month
            on meters associated with the well.
          </li>
          <li>
            <strong>Attachments</strong>: photos are embedded as a photo
            appendix; non-photo documents uploaded that day are listed by
            filename only.
          </li>
        </ul>
      </Alert>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <BlankTemplateButtons />
      </Box>

      <TableWrapper>
        <MaterialTable
          id="Well Inspections"
          title="Field Services Inspection Records"
          loading={isFetching || !data}
          columns={columns}
          data={data || []}
          components={{
            Container: (props) => <div {...props} />,
          }}
          options={{
            emptyRowsWhenPaging: false,
            exportAllData: true,
            columnsButton: true,
            exportButton: { csv: true },
            exportFileName: "well-inspections",
            pageSize: 30,
            pageSizeOptions: [10, 30, 60, 120],
            padding: "dense",
            searchFieldAlignment: "left",
            showTitle: true,
            filtering: true,
            headerStyle: {
              whiteSpace: "nowrap",
              fontWeight: 600,
            },
            cellStyle: {
              whiteSpace: "nowrap",
            },
          }}
          localization={{
            toolbar: { searchPlaceholder: "Search inspections" },
          }}
        />
      </TableWrapper>
    </React.Fragment>
  );
};

export default InspectionsManagement;
